import {
  faExclamationCircle,
  faInfoCircle,
  faTrashCan,
  faUser,
  faUserGroup,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Card,
  ClearIcon,
  ConfirmationModal,
  Divider,
  Flex,
  Heading,
  InlineAlert,
  Input,
  Text,
  TextButton,
  TextLink,
  TextWithIcon,
  Toast,
  Tooltip,
  useOpenModal,
  ValidationMessage,
} from '@modusign/design-system';
import {
  getAdminMember,
  getListWorkspacesQueryKey,
  listWorkspaces,
  useChangeWorkspaceToPublic,
  useUpdateWorkspace,
} from '@modusign/openapi-client-user';
import { ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form-v7';
import { useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import useAuthority from '@app/hooks/useAuthority';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import useUsageCount from '@app/hooks/useUsageCount';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useCurrentUserRole, useWorkspace } from '@app/hooks/workspace';
import pushAdobeDataLayer from '@app/libs/analytics/adobe';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import josa from '@app/libs/josa';
import workspaceService from '@app/libs/workspaceService';
import { getName } from '@app/store/user/user/selectors';

import { useOtherWorkspace } from '../../../../hooks/workspace';
import ChangePrivateWorkspaceToPublicWorkspaceConfirmModal from './ChangePrivateWorkspaceToPublicWorkspaceConfirmModal';

export default function WorkspaceSettingCard() {
  const queryClient = useQueryClient();
  const workspace = useWorkspace();
  const role = useCurrentUserRole();
  const userName = useSelector(getName);
  const history = useHistory();
  const { data, isLoading, error, refetch } = useWorkspaceInfo();

  const isMobileWidth = useResponsiveValue([true, true, false]);

  const [isEditingName, setIsEditingName] = useState(false);

  if (error) {
    return (
      <Card height="200px" position="relative">
        불러오기 실패 <TextButton onClick={() => refetch()}>다시 시도하기</TextButton>
      </Card>
    );
  }

  if (isLoading || !data) {
    return (
      <Card height="200px" position="relative">
        <CenterLoadingSpinner />
      </Card>
    );
  }

  const showAlert = data.workspaces.count > 1 && workspace.type === 'PRIVATE';

  const editingEnabled = workspace.type === 'PUBLIC' && role?.type === 'ADMIN';

  return (
    <Card>
      <Heading>설정</Heading>
      {showAlert && (
        <InlineAlert sx={{ mt: 7 }} variant="warning">
          내 워크스페이스에서는 서명 요청, 문서 등의 데이터 생성이 제한됩니다.
        </InlineAlert>
      )}
      <Flex flexDirection="column" gap={6} mt={7}>
        {workspace.type === 'PUBLIC' && <Row title="종류">공용 워크스페이스</Row>}
        <Row title="이름">
          {isEditingName ? (
            <WorkspaceNameForm
              initialValue={workspace.name || ''}
              onCancel={() => setIsEditingName(false)}
              onComplete={() => {
                setIsEditingName(false);
                queryClient.invalidateQueries(getListWorkspacesQueryKey());
              }}
            />
          ) : (
            <>
              {workspace.name || `${userName}의 워크스페이스`}
              {role?.type === 'ADMIN' && (
                <TextButton ml="10px" onClick={() => setIsEditingName(true)}>
                  변경
                </TextButton>
              )}
            </>
          )}
        </Row>
        <Row title="최고 관리자" tooltip="워크스페이스의 모든 권한을 가진 멤버입니다.">
          <Flex
            alignItems={isMobileWidth ? 'start' : 'center'}
            flexDirection={isMobileWidth ? 'column' : 'row'}
            sx={{ gap: 6 }}
          >
            <Box>
              {data.adminMember.name}
              <Text color="textNeutralVeryLight" fontSize="sm">
                {`(${data.adminMember.userEmail})`}
              </Text>
            </Box>
            {editingEnabled && (
              <Box
                alignSelf="flex-start"
                as={Link}
                border="1px solid"
                borderColor="buttonBorderNeutralBase"
                borderRadius="3px"
                color="textNeutralLight"
                display={['none', null, 'inline-block']}
                flex="none"
                fontSize="sm"
                fontWeight="medium"
                px={5}
                py={2}
                to="/settings/workspace/change-owner"
              >
                변경
              </Box>
            )}
          </Flex>
        </Row>
      </Flex>
      <Divider direction="row" my={8} />
      <Flex justifyContent="space-between">
        {/* 유저의 워크스페이스가 1개면 개인 워크스페이스 밖에 없습니다. */}
        {/* 이때 공용 워크스페이스로 전환하는 버튼을, 2개 이상이면 워크스페이스 사이를 이동하는 버튼을 보여줍니다. */}
        {data.workspaces.count === 1 ? (
          <ConvertPrivateWorkspaceToPublicButtons />
        ) : (
          <WorkspaceToggleButton />
        )}
        {editingEnabled && (
          <TextButton
            display={['none', null, 'block']}
            onClick={() => {
              history.push('/settings/workspace/delete');
            }}
          >
            <TextWithIcon leftIcon={<FontAwesomeIcon icon={faTrashCan} />}>
              워크스페이스 삭제
            </TextWithIcon>
          </TextButton>
        )}
      </Flex>
    </Card>
  );
}

function Row({
  title,
  tooltip,
  children,
}: {
  title: string;
  tooltip?: string;
  children: ReactNode;
}) {
  const isMobileWidth = useResponsiveValue([true, true, false]);

  return (
    <Flex
      alignItems={isMobileWidth ? 'start' : 'center'}
      flexDirection={isMobileWidth ? 'column' : 'row'}
      sx={{ gap: '10px' }}
    >
      <Text
        color="textNeutralLight"
        fontSize="sm"
        fontWeight="medium"
        sx={{ flex: 'none', width: '100px' }}
      >
        {title}
        {tooltip && (
          <Tooltip content={tooltip}>
            <Box as={FontAwesomeIcon} icon={faExclamationCircle} ml="5px" />
          </Tooltip>
        )}
      </Text>
      <Box sx={{ wordBreak: 'normal' }} width={isMobileWidth ? '100%' : 'initial'}>
        {children}
      </Box>
    </Flex>
  );
}

function useWorkspaceInfo() {
  const workspaceId = useWorkspaceId();
  return useQuery({
    queryKey: ['workspaceSetting', workspaceId],
    queryFn: async () => {
      const [workspaces, adminMember] = await Promise.all([
        listWorkspaces(),
        getAdminMember(workspaceId),
      ]);

      return { workspaces, adminMember };
    },
  });
}

function WorkspaceToggleButton() {
  const otherWorkspace = useOtherWorkspace();

  if (!otherWorkspace) {
    return null;
  }

  return (
    <Button
      variant={otherWorkspace.type === 'PRIVATE' ? 'subtle' : 'basic'}
      onClick={() => {
        workspaceService.setWorkspaceId(otherWorkspace.id);
        pushAdobeDataLayer({
          event: 'moveToOtherWorkspace',
          otherWorkspace,
        });
        Toast.success({
          description:
            otherWorkspace.type === 'PRIVATE'
              ? '내 워크스페이스로 이동했습니다.'
              : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                `${josa(otherWorkspace.name!, '으로/로')} 이동했습니다.`,
        });
      }}
    >
      <Box
        as={FontAwesomeIcon}
        fontSize="14px"
        icon={otherWorkspace.type === 'PRIVATE' ? faUser : faUserGroup}
        mr="3px"
      />
      {otherWorkspace.type === 'PRIVATE' ? '내 워크스페이스로 가기' : '공용 워크스페이스로 가기'}
    </Button>
  );
}

function ConvertPrivateWorkspaceToPublicButtons() {
  const { createErrorHandler } = useOpenErrorModal();
  const queryClient = useQueryClient();
  const openModal = useOpenModal();
  const userName = useSelector(getName);
  const workspaceId = useWorkspaceId();
  const authority = useAuthority();
  const { workspaceSeatCount } = useUsageCount();

  const { mutate, isLoading } = useChangeWorkspaceToPublic({
    mutation: {
      onSuccess: () => {
        pushDataLayer({ event: 'changeWorkspaceToPublic' });
        Toast.success({ description: '공용 워크스페이스로 전환했습니다.' });
        queryClient.invalidateQueries();
      },
      onError: createErrorHandler({ title: '워크스페이스 전환 실패' }),
    },
  });

  const openConfirmModal = () => {
    if (workspaceSeatCount === null || workspaceSeatCount.limit < 2) {
      openModal((props) => (
        <ConfirmationModal
          closeButtonLabel="확인"
          // tagContactButton 클래스로 채널톡을 엽니다.
          confirmButtonClassName="tagContactButton"
          confirmButtonLabel="요금제 상담"
          maxWidth="xs"
          title="요금제 상담 필요"
          variant="warning"
          onConfirm={props.onClose}
          {...props}
        >
          <Text as="p" textAlign="center">
            공용 워크스페이스로 전환하려면
            <br />
            문의를 통해 초대 인원을 추가해 주세요.
          </Text>
        </ConfirmationModal>
      ));
      return;
    }

    openModal(({ onClose }) => (
      <ChangePrivateWorkspaceToPublicWorkspaceConfirmModal
        onCancel={onClose}
        onConfirm={() => {
          mutate({ workspaceId, data: { name: `${userName}의 워크스페이스` } });
          onClose();
        }}
      />
    ));
  };

  return (
    <Flex flexDirection="column">
      {(isLoading || authority.isLoading) && <GlobalLoadingSpinner />}
      <Tooltip
        content={
          <Text fontSize="sm">
            다른 사람들을 초대하여
            <br />
            함께 모두싸인에서 협업할 수 있어요.
          </Text>
        }
      >
        <Button whiteSpace="pre" onClick={openConfirmModal}>
          <Box as={FontAwesomeIcon} fontSize="14px" icon={faUserGroup} mr="3px" />
          공용 워크스페이스로 전환
        </Button>
      </Tooltip>
      <TextLink href="https://support.modusign.co.kr/114456043101" mt={6} target="_blank">
        <TextWithIcon leftIcon={<FontAwesomeIcon icon={faInfoCircle} />}>
          공용 워크스페이스란?
        </TextWithIcon>
      </TextLink>
    </Flex>
  );
}

function WorkspaceNameForm({
  initialValue,
  onCancel,
  onComplete,
}: {
  initialValue: string;
  onComplete: () => void;
  onCancel: () => void;
}) {
  const workspaceId = useWorkspaceId();
  const isMobileWidth = useResponsiveValue([true, true, false]);

  const { mutate } = useUpdateWorkspace({
    mutation: {
      onSuccess: () => {
        Toast.success({ description: '워크스페이스 이름을 변경했습니다.' });
        onComplete();
      },
      onError: () => {
        Toast.error({ description: '워크스페이스 이름 변경을 실패했습니다.' });
      },
    },
  });
  const { register, handleSubmit, formState, setValue } = useForm<{ name: string }>({
    mode: 'onChange',
  });

  return (
    <Box
      as="form"
      onSubmit={handleSubmit((data) =>
        mutate({
          workspaceId,
          data: { name: data.name },
        })
      )}
    >
      <Flex
        alignItems={isMobileWidth ? 'start' : 'center'}
        flexDirection={isMobileWidth ? 'column' : 'row'}
        sx={{ gap: '10px' }}
      >
        <Box sx={{ position: 'relative' }} width={isMobileWidth ? '100%' : '274px'}>
          <Input
            defaultValue={initialValue}
            isInvalid={!!formState.errors.name}
            {...register('name', { required: '워크스페이스 이름 입력은 필수입니다.' })}
            sx={{ paddingRight: 8 }}
          />
          <Box
            as="button"
            sx={{
              position: 'absolute',
              right: 3,
              top: 2,
            }}
            onClick={() => setValue('name', '')}
          >
            <ClearIcon />
          </Box>
        </Box>
        {isMobileWidth && formState.errors.name && (
          <ValidationMessage>{formState.errors.name.message}</ValidationMessage>
        )}
        <Flex sx={{ gap: '10px' }}>
          <TextButton onClick={onCancel}>취소</TextButton>
          <TextButton disabled={!formState.isValid} type="submit">
            적용
          </TextButton>
        </Flex>
      </Flex>
      {!isMobileWidth && formState.errors.name && (
        <ValidationMessage>{formState.errors.name.message}</ValidationMessage>
      )}
    </Box>
  );
}
